import {
  useContext,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'

import { Box, BoxProps, Grid } from '@mantine/core'

import CarouselCard from 'components/atoms/CarouselCardV2/'
import { CarouselCardHandle } from 'components/atoms/CarouselCardV2/CarouselCard'
import GroupItems from 'components/atoms/groupItems'

import { MAX_COL } from '../../constants'
import { CtaBannerContext } from '../../context'
import { configGrid } from '../../helpers'
import { MultipleBanner } from '../../index.types'
import { Item } from '../Item'

type Props = {
  banners: MultipleBanner['listing'][number]['banners']
  keepGridCol?: boolean
  carouselProps?: typeof CarouselCard
} & BoxProps

export const CarouselLayout = forwardRef<CarouselCardHandle, Props>(
  (
    { styles, classNames, banners = [], keepGridCol, ...carouselProps },
    ref,
  ) => {
    const { data } = useContext(CtaBannerContext)
    const { layout } = (data || {}) as MultipleBanner
    const configObj = useMemo(() => configGrid(banners.length, layout), [
      banners,
    ])
    const carouselRef = useRef<CarouselCardHandle>(null)

    useImperativeHandle(ref, () => carouselRef.current!, [])

    return (
      <Box
        sx={(theme) => ({
          position: 'relative',
          '.arrow': {
            position: 'absolute',
            top: '50%',
            transform: 'translate(0, -50%)',
            border: `1px solid ${theme.colors.gray[1]}`,
            width: 40,
            height: 40,
            boxShadow:
              '0px 0px 1px 0px #0000000A,0px 2px 8px 0px #0000000A,0px 10px 16px 0px #0000000A',
            '&.arrow-prev': {
              left: -64,
            },
            '&.arrow-next': {
              right: -64,
            },
            [theme.fn.smallerThan('sm')]: {
              display: 'none',
            },
          },
          [theme.fn.smallerThan('sm')]: {
            overflow: 'hidden',
            '.carousel-progress': {
              width: `calc(100% - 32px)`,
            },
          },
        })}
      >
        <CarouselCard
          slideSize="100%"
          slideGap={24}
          align="start"
          autoplay
          withControls
          ref={carouselRef}
          breakpoints={[{ maxWidth: 'sm', slideSize: '80%', slideGap: 16 }]}
          {...carouselProps}
        >
          <CarouselCard.Body>
            <GroupItems
              breakpoints={{
                base: {
                  itemsPerGroup: 1,
                  GroupWrapper: (children) => (
                    <CarouselCard.Slide>{children}</CarouselCard.Slide>
                  ),
                },
                md: {
                  itemsPerGroup: MAX_COL,
                  GroupWrapper: (children) => (
                    <CarouselCard.Slide
                      sx={{
                        paddingBottom: 30,
                      }}
                    >
                      <Grid
                        gutter={configObj.gap}
                        grow={keepGridCol ? false : configObj.grow}
                      >
                        {children}
                      </Grid>
                    </CarouselCard.Slide>
                  ),
                  ItemWrapper: (children) => (
                    <Grid.Col span={configObj.span}>{children}</Grid.Col>
                  ),
                },
              }}
              items={banners.map((banner, index) => (
                <Item
                  key={String(index)}
                  data={banner}
                  layout={layout}
                  infoLayout={
                    banners.length <= 2 && !keepGridCol ? 'horizon' : 'vertical'
                  }
                  radius={banners.length > 2 ? 16 : 24}
                  ratio={configObj.ratio}
                />
              ))}
            />
          </CarouselCard.Body>
          <CarouselCard.Control className="arrow arrow-prev" direction="prev" />
          <CarouselCard.Control className="arrow arrow-next" direction="next" />
          <CarouselCard.Progress
            className="carousel-progress"
            sx={(theme) => ({
              marginTop: 16,
              [theme.fn.largerThan('sm')]: {
                display: 'none',
              },
            })}
          />
        </CarouselCard>
      </Box>
    )
  },
)

CarouselLayout.displayName = 'CarouselLayout'
